// ════════════════════════════════════════════════════════════════════════════
// LpaThemeToggle — switch light/dark com persistência em localStorage
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import { setTheme, getStoredTheme } from "../../../shared/tokens";

export interface LpaThemeToggleProps {
  /** Callback quando o tema muda */
  onChange?: (theme: "light" | "dark") => void;
  /** Tamanho */
  size?: "sm" | "md";
  /** Esconder labels (só icones) */
  iconOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export default function LpaThemeToggle({
  onChange,
  size = "md",
  iconOnly = false,
  className,
  style,
}: LpaThemeToggleProps): React.ReactElement {
  const [theme, setLocalTheme] = useState<"light" | "dark">(() => getStoredTheme());

  useEffect(() => {
    setTheme(theme);
    onChange?.(theme);
  }, [theme, onChange]);

  const isDark = theme === "dark";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={`Alternar para tema ${isDark ? "claro" : "escuro"}`}
      onClick={() => setLocalTheme(isDark ? "light" : "dark")}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: size === "sm" ? "5px 10px" : "7px 12px",
        background: "transparent",
        color: "var(--lpa-muted)",
        border: "1px solid var(--lpa-border)",
        borderRadius: "var(--lpa-radius-md)",
        cursor: "pointer",
        fontFamily: "var(--lpa-font-mono)",
        fontSize: size === "sm" ? "var(--lpa-text-2xs)" : "var(--lpa-text-xs)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        transition: "all var(--lpa-tr-fast)",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--lpa-border-strong)";
        e.currentTarget.style.color = "var(--lpa-text)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--lpa-border)";
        e.currentTarget.style.color = "var(--lpa-muted)";
      }}
    >
      <span aria-hidden="true">{isDark ? "☾" : "☀"}</span>
      {!iconOnly && <span>{isDark ? "Dark" : "Light"}</span>}
    </button>
  );
}
